import { useState } from "react";
import { toast } from "sonner";
import { Phone, Send } from "lucide-react";
import Reveal, { Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  facility_name: "",
  facility_type: "ICF",
  facility_size: "Small (1-15 beds)",
  contact_name: "",
  email: "",
  phone: "",
  service_needed: "DOJ Extension / Stay",
  citation_status: "",
  target_deadline: "",
  notes: "",
};

const inputClass =
  "w-full rounded-xl border border-sand/15 bg-basalt/60 px-4 py-3 text-sm text-sand placeholder:text-smoke outline-none transition-[border-color,box-shadow] duration-300 focus:border-ochre/60 focus:ring-2 focus:ring-ochre/20";

const labelClass = "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mist";

export default function IntakeForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      toast.success("Intake received. James or Maria will contact you within one business day.");
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong. Please call us directly at 808-491-3395.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="intake" className="bg-abyss py-28 lg:py-40" data-testid="intake-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal>
          <Eyebrow>Emergency compliance intake</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-sand sm:text-3xl lg:text-4xl" data-testid="intake-heading">
            Facing a citation or deadline? Start here.
          </h2>
          <p className="mt-5 max-w-lg text-base text-mist">
            Tell us where your facility stands. We review every intake personally and respond
            within one business day — usually faster when a DOJ deadline is involved.
          </p>
          <div className="mt-10 space-y-4 border-l-2 border-ochre/40 pl-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
              Prefer to talk now?
            </p>
            <a href="tel:+18084913395" className="flex items-center gap-3 text-sand transition-colors duration-300 hover:text-ochre" data-testid="intake-phone-james">
              <Phone size={16} className="text-ochre" /> James Bordenave — 808-491-3395
            </a>
            <a href="tel:+18083154667" className="flex items-center gap-3 text-sand transition-colors duration-300 hover:text-ochre" data-testid="intake-phone-maria">
              <Phone size={16} className="text-ochre" /> Maria Lopez — 808-315-4667
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="glass-card rounded-2xl p-8 lg:p-10" data-testid="intake-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="facility_name">Facility Name *</label>
                <input id="facility_name" required className={inputClass} value={form.facility_name} onChange={set("facility_name")} placeholder="e.g. Aloha Care Home" data-testid="intake-facility-name-input" />
              </div>
              <div>
                <label className={labelClass} htmlFor="facility_type">Facility Type *</label>
                <select id="facility_type" className={inputClass} value={form.facility_type} onChange={set("facility_type")} data-testid="intake-facility-type-select">
                  <option>ICF</option>
                  <option>IDD</option>
                  <option>ICF/IDD (Both)</option>
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="facility_size">Facility Size *</label>
                <select id="facility_size" className={inputClass} value={form.facility_size} onChange={set("facility_size")} data-testid="intake-facility-size-select">
                  <option>Small (1-15 beds)</option>
                  <option>Large (16+ beds)</option>
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="contact_name">Contact Name *</label>
                <input id="contact_name" required className={inputClass} value={form.contact_name} onChange={set("contact_name")} placeholder="Your full name" data-testid="intake-contact-name-input" />
              </div>
              <div>
                <label className={labelClass} htmlFor="phone">Phone *</label>
                <input id="phone" required type="tel" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="808-000-0000" data-testid="intake-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="email">Email *</label>
                <input id="email" required type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@facility.com" data-testid="intake-email-input" />
              </div>
              <div>
                <label className={labelClass} htmlFor="service_needed">Service Needed *</label>
                <select id="service_needed" className={inputClass} value={form.service_needed} onChange={set("service_needed")} data-testid="intake-service-select">
                  <option>DOJ Extension / Stay</option>
                  <option>Plan of Correction (POC)</option>
                  <option>Plan of Implementation (POI)</option>
                  <option>SAPS Development</option>
                  <option>Data Reading & Analysis</option>
                  <option>Multiple / Not Sure</option>
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="target_deadline">Target Deadline</label>
                <input id="target_deadline" type="date" className={inputClass} value={form.target_deadline} onChange={set("target_deadline")} data-testid="intake-deadline-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="citation_status">Current Citation / DOJ Status</label>
                <input id="citation_status" className={inputClass} value={form.citation_status} onChange={set("citation_status")} placeholder="e.g. Received notice of non-compliance on..." data-testid="intake-citation-input" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass} htmlFor="notes">Notes / Citation Details</label>
                <textarea id="notes" rows={4} className={inputClass} value={form.notes} onChange={set("notes")} placeholder="Paste citation text or describe your situation..." data-testid="intake-notes-textarea" />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-ochre px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-basalt transition-[background-color,transform,opacity] duration-300 hover:bg-ochre-light hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="intake-submit-button"
            >
              <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
              {submitting ? "Submitting..." : "Submit Compliance Intake"}
            </button>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
              Backed by our money-back guarantee
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
